<template>
  <div class="coord-picker">
    <div class="map-container" :style="`bottom:${bottom}`">
      <amap
        ref="wrapper"
        cache-key="coord-picker-map"
        mmap-style="amap://styles/whitesmoke"
        async
        :center.sync="center"
        :zoom.sync="zoom"
        is-hotspot
        @click="onPointChange"
      >
        <amap-satellite-layer :visible="satellite" />

        <amap-marker
          v-if="position"
          :position.sync="position"
          draggable
          @dragend="onPointChange"
        />

        <el-card class="result-panel">
          <template slot="header">
            <template v-if="mode === 'search'">
              <el-autocomplete
                popper-class="my-autocomplete"
                v-model="query"
                placeholder="请输入内容"
                style="width:100%;"
                :fetch-suggestions="autoComplete"
              >
                <template slot="append">
                  <el-button
                    type="primary"
                    :disabled="!query"
                    @click="search(true)"
                    >搜索</el-button
                  >
                </template>
                <template slot-scope="{ item }">
                  <div class="name">{{ item.name }}</div>
                  <span class="addr">{{ item.address }}</span>
                </template>
              </el-autocomplete>
            </template>
            <template v-if="mode === 'result'">
              <div class="search-bar">
                <i
                  class="el-icon-arrow-left"
                  @click="reset"
                  style="margin-right: 6px;font-size: 20px;cursor: pointer;"
                ></i>
                <span class="text"
                  >搜索 {{ query }} 共
                  {{ searching ? '...' : total }} 条结果</span
                >
              </div>
            </template>
          </template>

          <div
            v-if="mode === 'result'"
            :style="
              `max-height:${panelMaxHeight};overflow-y;auto;overflow-x:hidden;padding:20px;`
            "
          >
            <el-pagination
              v-if="total > 0"
              small
              :current-page.sync="pageIndex"
              :page-size="pageSize"
              layout="prev, pager, next"
              :total="total"
            >
            </el-pagination>
            <div
              class="list-item"
              v-for="(item, index) in results"
              :key="index"
            >
              <div class="title">
                <span style="cursor: pointer;" @click="focus(item)">{{
                  item.name
                }}</span>
              </div>
              <div class="description">{{ item.address }}</div>
            </div>
            <div class="mt-3">
              <el-pagination
                v-if="total > 0"
                small
                :current-page.sync="pageIndex"
                :page-size="pageSize"
                layout="prev, pager, next"
                :total="total"
              >
              </el-pagination>
            </div>
          </div>
        </el-card>

        <div class="card info">
          <el-input readonly v-model="positionText" />
        </div>
      </amap>
    </div>
  </div>
</template>

<script>
import { loadAmap, loadPlugins } from '@amap/amap-vue'

export default {
  props: {
    city: String,
    address: {
      type: String,
      default: ''
    },
    // 底部距离
    bottom: {
      type: String,
      default: '10px'
    },
    panelMaxHeight: {
      type: String,
      default: '350px'
    },
    location: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      mode: 'search',
      center:
        this.location || this.location.split(',')[1]
          ? this.location.split(',')
          : null,
      zoom: 18,
      query: this.address,
      searching: false,
      tips: [],
      results: [],
      position: this.location ? this.location.split(',') : null,
      pageIndex: 1,
      pageSize: 10,
      total: 0,
      satellite: false,
      ps: {},
      ac: {},
      geolocation: {},
      geocoder: {}
    }
  },
  computed: {
    wrapper() {
      return this.$refs.wrapper
    },
    positionText() {
      if (!this.position) return ''
      if (!this.position[1]) return ''
      return `${this.position[0]},${this.position[1]}`
    },
    markLocation() {
      return this.location || this.location.split(',')[1]
        ? this.location.split(',')
        : null
    }
  },
  watch: {
    pageIndex(value) {
      this.ps.setPageIndex(value)
      this.search(false)
    }
  },
  async created() {
    await this.init()
  },
  methods: {
    async init() {
      const AMap = await loadAmap()
      // 加载需要用到的插件
      await loadPlugins([
        'AMap.PlaceSearch',
        'AMap.AutoComplete',
        'AMap.Geolocation',
        'AMap.Geocoder'
      ])

      this.setGeolocation(AMap)
      // 创建POI搜索对象
      this.ps = new AMap.PlaceSearch({
        pageSize: this.pageSize
      })
      // 创建输入提示对象
      this.ac = new AMap.AutoComplete()

      // 创建地理编码与逆地理编码对象
      this.geocoder = new AMap.Geocoder({
        radius: 1000
      })
    },
    /**
     * 浏览器精准定位
     */
    setGeolocation(AMap) {
      this.geolocation = new AMap.Geolocation({
        // 是否使用高精度定位，默认：true
        enableHighAccuracy: true,
        // 设置定位超时时间，默认：无穷大
        timeout: 10000,
        // 定位按钮的停靠位置的偏移量，默认：Pixel(10, 20)
        buttonOffset: new AMap.Pixel(10, 20),
        //  定位成功后调整地图视野范围使定位位置及精度范围视野内可见，默认：false
        zoomToAccuracy: true,
        //  定位按钮的排放位置,  RB表示右下
        buttonPosition: 'RB',
        panToLocation: true
      })

      this.wrapper.$map.addControl(this.geolocation)
      this.geolocation.getCurrentPosition((status, result) => {
        if (status !== 'complete') {
          console.error('getCurrentPosition error-->', result)
        }

        // 如果打点坐标存在 则将中心点定位到打点位置
        if (this.markLocation) this.wrapper.$map.setCenter(this.markLocation)
      })
    },
    /**
     * 打点坐标发生变化时
     */
    async onPointChange(e) {
      if (!e.lnglat) {
        this.position = null
        return
      }

      this.position = [e.lnglat.lng, e.lnglat.lat]
      // 根据坐标获取详细地址
      const address = await this.getFormattedAddress(this.position)
        .then((res) => res)
        .catch((e) => console.error(e.message))
      if (!this.address && address) this.query = address

      this.$emit('change', {
        address: this.query,
        location: this.position.join(','),
        lng: this.position[0],
        lat: this.position[1]
      })
    },
    async search(clear = false) {
      this.mode = 'result'

      if (clear) {
        this.results = []
        this.total = 0
        this.pageIndex = 1
        this.ps.setPageIndex(1)
      }

      this.searching = true

      // 限定城市范围
      if (this.city) {
        this.ps.setCity(this.city)
        this.ps.setCityLimit(true)
      }

      this.ps.search(this.query, (status, result) => {
        this.searching = false

        if (status === 'complete' && result.poiList) {
          this.results = result.poiList.pois
          this.total = result.poiList.count
        } else {
          this.results = []
          this.total = 0
        }
      })
    },
    autoComplete(kw, cb = () => {}) {
      if (!kw) {
        this.tips = []
        cb(this.tips)
      } else {
        if (this.city) {
          this.ac.setCity(this.city)
          this.ac.setCityLimit(true)
        }
        this.ac.search(kw, (status, result) => {
          if (kw !== this.query) return
          if (status === 'complete' && result.tips) {
            this.tips = Array.from(
              new Set(
                result.tips
                  .filter((tip) => tip.id)
                  .map((tip) => {
                    const address =
                      tip.address && tip.address.length ? tip.address : tip.name
                    return {
                      name: tip.name,
                      value: address,
                      address
                    }
                  })
              )
            )
          } else {
            this.tips = []
          }
          cb(this.tips)
        })
      }
    },
    focus(poi) {
      const pos = [poi.location.lng, poi.location.lat]
      this.position = [...pos]
      this.center = [...pos]
      this.$emit('change', {
        address: this.address || poi.name,
        location: pos.join(','),
        lng: pos[0],
        lat: pos[1]
      })
    },
    reset() {
      this.ps.setPageIndex(1)
      this.results = []
      this.tips = []
      this.total = 0
      this.mode = 'search'
    },
    /**
     * 根据经纬度获取结构化地址信息
     */
    getFormattedAddress(location) {
      return new Promise((resolve, reject) => {
        this.geocoder.getAddress(location, function(status, result) {
          if (status === 'complete' && result.regeocode) {
            const address = result.regeocode.formattedAddress
            resolve(address)
          } else {
            reject({
              location,
              message: result.info
            })
          }
        })
      })
    }
  }
}
</script>

<style lang="scss">
.my-autocomplete {
  width: 100%;
  li {
    line-height: normal;
    padding-top: 7px;
    padding-bottom: 7px;

    .name {
      text-overflow: ellipsis;
      overflow: hidden;
    }
    .addr {
      font-size: 12px;
      color: #b4b4b4;
    }

    .highlighted .addr {
      color: #ddd;
    }
  }
}
.result-panel {
  .el-card__body {
    padding: initial !important;
  }
}
</style>

<style lang="scss" scoped>
.coord-picker {
  position: relative;
}
.card {
  padding: 10px;
  background: #ffffff;
}
.map-container {
  width: 100%;
  position: absolute;
  top: 0;
  bottom: 10px;
}

.result-panel {
  position: absolute;
  left: 10px;
  top: 10px;
  width: 500px;
  display: flex;
  flex-direction: column;

  .search-bar {
    display: flex;
    align-items: center;
    .text {
      text-overflow: ellipsis;
      flex: 1;
      overflow: hidden;
      white-space: nowrap;
    }
  }

  .list-item {
    border-bottom: 1px solid #e8e8e8;
    padding: 8px 0;
    .title {
      //   font-weight: 700;
      margin-bottom: 12px;
      color: rgba(0, 0, 0, 0.85);
      font-size: 16px;
      line-height: 24px;
    }
    .description {
      color: rgba(0, 0, 0, 0.45);
      font-size: 14px;
      line-height: 22px;
    }
  }
  .result-list.ant-list-loading {
    min-height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
.info {
  position: absolute;
  right: 10px;
  top: 10px;
  // padding-left: 24px;
  background-color: transparent;
  border: 0;
  max-width: 300px;
}
</style>
